import React, { FC } from 'react'
import { TransitionProps, Stepper, Step, StepContent } from 'transfix-ui/core'
import { makeStyles, Theme, createStyles } from 'transfix-ui/core/styles'
import { brandColors } from 'transfix-ui/theme/colors'
import { useFeatureFlagContext } from 'utils/context/FeatureFlagsContext'
import { useCreateLoadFormContext } from 'apps/fleet-planner/context'
import { useWatchLoad } from 'apps/fleet-planner/hooks'
import { SidebarStepConnector } from './SidebarStepConnector'
import { SidebarStepContent } from './SidebarStepContent'
import { SidebarStepLabel } from './SidebarStepLabel'
import { createValueGetter, getStepsArray, getPathsFromRequiredFields } from './stepUtils'

const useSidebarSummaryStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      width: '100%',
    },
    button: {
      marginTop: theme.spacing(1),
      marginRight: theme.spacing(1),
    },
    actionsContainer: {
      marginBottom: theme.spacing(2),
    },
    resetContainer: {
      padding: theme.spacing(3),
    },
    item: {
      paddingTop: 0,
    },
    stepContent: {
      minHeight: theme.spacing(2),
      marginTop: `calc(${theme.spacing(1)} / 2)`,
      marginLeft: `calc(${theme.spacing(1)} - 1px)`,
      paddingLeft: theme.spacing(3),
      paddingBottom: theme.spacing(2),
      borderLeft: `2px solid ${brandColors.coolGray4}`,
    },
    stepper: {
      position: 'relative',
      '&::before': {
        borderRadius: 4,
        display: 'block',
        content: '" "',
        position: 'absolute',
        top: `-${theme.spacing(2)}`,
        left: `-${theme.spacing(1)}`,
        right: `-${theme.spacing(1)}`,
        bottom: theme.spacing(1),
        backgroundColor: brandColors.coolGray1,
        zIndex: 0,
        opacity: 0,
      },
      '&:last-child::before': {
        bottom: `-${theme.spacing(2)}`,
      },
      '&:hover': {
        cursor: 'pointer',
      },
      '&:hover::before': {
        opacity: 1,
      },
      '&:hover > span > .MuiStepLabel-labelContainer': {
        color: brandColors.skyBlue6,
        fontWeight: 'bold',
      },
      '&:hover .MuiStepIcon-root': {
        border: `2px solid ${brandColors.skyBlue6}`,
      },
      '& > *': {
        position: 'relative',
        zIndex: 10,
      },
    },
  })
)

const TransitionComponent: FC<TransitionProps> = ({ children }) => {
  return <div>{children}</div>
}

interface ISidebarSummaryProps {
  onSetActiveStep: (step: number) => void
  activeStep: number
}

export const SidebarSummary: FC<ISidebarSummaryProps> = ({ activeStep, onSetActiveStep }) => {
  const { control, errors } = useCreateLoadFormContext()
  const classes = useSidebarSummaryStyles()
  const { getFlag } = useFeatureFlagContext()
  const { enabled: isFpCarrierPortalEnabled } = getFlag('fpCarrierPortal')

  /**
   * Smooth scroll to step in DOM
   */
  const handleScrollToStep = (step: number, classId: string) => () => {
    const element = document.getElementById(classId)
    if (element) {
      element.scrollIntoView({
        block: 'start',
        behavior: 'smooth',
      })
    }
    onSetActiveStep(step)
  }

  const values = useWatchLoad({
    control,
  })

  const { stops = [] } = values || {}
  const getFormValues = createValueGetter({ errors, values })

  const steps = getStepsArray(stops, isFpCarrierPortalEnabled)

  return (
    <Stepper orientation='vertical' className={classes.root} connector={<SidebarStepConnector />}>
      {steps.map(({ label, fields = [], classId }, index) => {
        const isActive = index === activeStep
        const isNotLastStep = index !== steps.length - 1
        const fieldsArray = getPathsFromRequiredFields(fields)
        const { error: labelError, value: labelValue } = getFormValues(fieldsArray) || {}
        return (
          <Step
            key={label}
            onClick={handleScrollToStep(index, classId)}
            className={classes.stepper}>
            <SidebarStepLabel
              label={label}
              values={labelValue}
              errors={labelError}
              isActive={isActive}
              fieldsLength={fields.length}
            />
            {isNotLastStep && (
              <StepContent
                TransitionComponent={TransitionComponent}
                className={classes.stepContent}>
                {fields.map(item => {
                  const value = getFormValues(item.fields, item.label, item.keyName)
                  return <SidebarStepContent key={item.label} value={value} />
                })}
              </StepContent>
            )}
          </Step>
        )
      })}
    </Stepper>
  )
}
